import { REGION_BLEND_WIDTH, REGION_CELL_SIZE, WORLD_EXTENT, voxels } from "./constants";
import { hash32, hashFloat } from "./random";
import type {
  BiomeId,
  BiomeWeight,
  RegionBiomeDefinition,
  RegionGraph,
  RegionNode,
  RegionSetDefinition,
  SiteChoice,
  SiteSlot,
} from "./types";

function freezeBiomeDefinition(definition: RegionBiomeDefinition): RegionBiomeDefinition {
  return Object.freeze({
    ...definition,
    siteCount: Object.freeze([definition.siteCount[0], definition.siteCount[1]] as const),
    sites: Object.freeze(definition.sites.map((site) => Object.freeze({ ...site }))),
  });
}

/** The shipped Alpha selection. The graph algorithm has no Alpha-specific branches. */
export const ALPHA_REGION_SET: RegionSetDefinition = Object.freeze({
  id: "alpha",
  coastDistance: voxels(150),
  coast: freezeBiomeDefinition({
    biome: "coast",
    weight: 1,
    siteCount: [1, 1],
    sites: [{ kind: "harbor", prefabId: null, weight: 1 }],
  }),
  interior: Object.freeze([
    freezeBiomeDefinition({
      biome: "forest",
      weight: 1,
      siteCount: [3, 3],
      sites: [
        { kind: "ruin", prefabId: "forest-grove", weight: 1 },
        { kind: "farm", prefabId: "forest-grove", weight: 1 },
        { kind: "enemy-camp", prefabId: "forest-grove", weight: 1 },
        { kind: "dungeon-mouth", prefabId: "forest-grove", weight: 1 },
      ],
    }),
    freezeBiomeDefinition({
      biome: "plains",
      weight: 1,
      siteCount: [2, 2],
      sites: [
        { kind: "ruin", prefabId: "stone-wayshrine", weight: 1 },
        { kind: "farm", prefabId: "stone-wayshrine", weight: 1 },
        { kind: "enemy-camp", prefabId: "stone-wayshrine", weight: 1 },
        { kind: "dungeon-mouth", prefabId: "stone-wayshrine", weight: 1 },
      ],
    }),
  ]),
});

function validateRegionSet(regionSet: RegionSetDefinition): void {
  const definitions = [regionSet.coast, ...regionSet.interior];
  if (regionSet.interior.length === 0 || regionSet.coastDistance < 0) throw new RangeError("invalid region set");
  for (const definition of definitions) {
    const [minimum, maximum] = definition.siteCount;
    if (!(definition.weight > 0) || !Number.isInteger(minimum) || !Number.isInteger(maximum)
      || minimum < 0 || maximum < minimum || (maximum > 0 && definition.sites.length === 0)
      || definition.sites.some((site) => !(site.weight > 0))) {
      throw new RangeError(`invalid region definition for ${definition.biome}`);
    }
  }
}

function weightedChoice<T extends { readonly weight: number }>(seedValue: number, choices: readonly T[]): T {
  const total = choices.reduce((sum, choice) => sum + choice.weight, 0);
  let cursor = (seedValue / 0x1_0000_0000) * total;
  for (const choice of choices) {
    cursor -= choice.weight;
    if (cursor < 0) return choice;
  }
  const fallback = choices[choices.length - 1];
  if (!fallback) throw new RangeError("weighted choice requires at least one option");
  return fallback;
}

function regionDefinition(
  seed: number,
  gridX: number,
  gridZ: number,
  x: number,
  z: number,
  regionSet: RegionSetDefinition,
): RegionBiomeDefinition {
  const edge = Math.min(x, z, WORLD_EXTENT - 1 - x, WORLD_EXTENT - 1 - z);
  if (edge < regionSet.coastDistance) return regionSet.coast;
  return weightedChoice(hash32(seed ^ 0x41c6ce57, gridX, 0, gridZ), regionSet.interior);
}

function createSites(
  seed: number,
  regionId: number,
  definition: RegionBiomeDefinition,
  centerX: number,
  centerZ: number,
): readonly SiteSlot[] {
  const [minimum, maximum] = definition.siteCount;
  const siteCount = minimum + hash32(seed ^ 0x78dde6e4, regionId) % (maximum - minimum + 1);
  const sites: SiteSlot[] = [];
  for (let index = 0; index < siteCount; index += 1) {
    const angleBucket = hash32(seed ^ 0x2f6e2b1, regionId, index) & 7;
    const directions = [[1, 0], [1, 1], [0, 1], [-1, 1], [-1, 0], [-1, -1], [0, -1], [1, -1]] as const;
    const direction = directions[angleBucket] ?? directions[0];
    const distance = voxels(12) + Math.floor(hashFloat(seed ^ 0xa511e9b3, regionId, index) * voxels(17));
    const margin = voxels(8);
    const x = Math.max(margin, Math.min(WORLD_EXTENT - margin - 1, Math.round(centerX + direction[0] * distance)));
    const z = Math.max(margin, Math.min(WORLD_EXTENT - margin - 1, Math.round(centerZ + direction[1] * distance)));
    const choice: SiteChoice = weightedChoice(hash32(seed ^ 0x335ea8af, regionId, index), definition.sites);
    sites.push(Object.freeze({
      id: hash32(seed ^ 0x6d2b79f5, regionId, index),
      kind: choice.kind,
      x,
      z,
      prefabId: choice.prefabId,
      rotation: (hash32(seed ^ 0x9b13, regionId, index) & 3) as 0 | 1 | 2 | 3,
    }));
  }
  return Object.freeze(sites);
}

/** Builds the complete finite seeded graph; no hidden mutable cache is used. */
export function createRegionGraph(seed: number, regionSet: RegionSetDefinition = ALPHA_REGION_SET): RegionGraph {
  validateRegionSet(regionSet);
  const normalizedSeed = seed >>> 0;
  const cells = Math.ceil(WORLD_EXTENT / REGION_CELL_SIZE);
  const regions: RegionNode[] = [];
  for (let gridZ = 0; gridZ < cells; gridZ += 1) {
    for (let gridX = 0; gridX < cells; gridX += 1) {
      const id = gridX + gridZ * cells;
      const minX = gridX * REGION_CELL_SIZE;
      const minZ = gridZ * REGION_CELL_SIZE;
      const cellWidth = Math.min(REGION_CELL_SIZE, WORLD_EXTENT - minX);
      const cellDepth = Math.min(REGION_CELL_SIZE, WORLD_EXTENT - minZ);
      const centerX = minX + cellWidth * (0.28 + hashFloat(normalizedSeed ^ 0xa24baed4, gridX, 0, gridZ) * 0.44);
      const centerZ = minZ + cellDepth * (0.28 + hashFloat(normalizedSeed ^ 0x9fb21c65, gridX, 0, gridZ) * 0.44);
      const definition = regionDefinition(normalizedSeed, gridX, gridZ, centerX, centerZ, regionSet);
      regions.push(Object.freeze({
        id,
        gridX,
        gridZ,
        centerX,
        centerZ,
        biome: definition.biome,
        sites: createSites(normalizedSeed, id, definition, centerX, centerZ),
      }));
    }
  }
  return Object.freeze({
    seed: normalizedSeed,
    extent: WORLD_EXTENT,
    regionSetId: regionSet.id,
    cellsPerAxis: cells,
    regions: Object.freeze(regions),
  });
}

const alphaGraphCache = new Map<number, RegionGraph>();

/** Reuses only deeply immutable Alpha graphs; eviction can never change output. */
export function alphaRegionGraph(seed: number): RegionGraph {
  const normalizedSeed = seed >>> 0;
  const cached = alphaGraphCache.get(normalizedSeed);
  if (cached) return cached;
  const graph = createRegionGraph(normalizedSeed, ALPHA_REGION_SET);
  alphaGraphCache.set(normalizedSeed, graph);
  if (alphaGraphCache.size > 8) {
    const oldest = alphaGraphCache.keys().next().value as number | undefined;
    if (oldest !== undefined) alphaGraphCache.delete(oldest);
  }
  return graph;
}

function nearestRegions(graph: RegionGraph, x: number, z: number): readonly { region: RegionNode; distance: number }[] {
  const gridX = Math.floor(x / REGION_CELL_SIZE);
  const gridZ = Math.floor(z / REGION_CELL_SIZE);
  const ranked: { region: RegionNode; distance: number }[] = [];
  for (let dz = -2; dz <= 2; dz += 1) {
    const candidateZ = gridZ + dz;
    if (candidateZ < 0 || candidateZ >= graph.cellsPerAxis) continue;
    for (let dx = -2; dx <= 2; dx += 1) {
      const candidateX = gridX + dx;
      if (candidateX < 0 || candidateX >= graph.cellsPerAxis) continue;
      const region = graph.regions[candidateX + candidateZ * graph.cellsPerAxis];
      if (!region) continue;
      const candidate = { region, distance: Math.hypot(x - region.centerX, z - region.centerZ) };
      let insertAt = ranked.length;
      while (insertAt > 0) {
        const previous = ranked[insertAt - 1];
        if (previous && (previous.distance < candidate.distance
          || (previous.distance === candidate.distance && previous.region.id < region.id))) break;
        insertAt -= 1;
      }
      if (insertAt < 4) ranked.splice(insertAt, 0, candidate);
      if (ranked.length > 4) ranked.pop();
    }
  }
  return ranked;
}

export function regionAt(graph: RegionGraph, x: number, z: number): RegionNode {
  const nearest = nearestRegions(graph, x, z)[0];
  if (!nearest) throw new RangeError("region graph is empty");
  return nearest.region;
}

/** Smooth weights around Voronoi borders, with a single region in each interior. */
export function biomeWeightsAt(graph: RegionGraph, x: number, z: number): readonly BiomeWeight[] {
  const nearest = nearestRegions(graph, x, z);
  const first = nearest[0];
  if (!first) return Object.freeze([{ biome: "plains", weight: 1 }]);
  const totals = new Map<BiomeId, number>();
  for (const candidate of nearest) {
    const separation = candidate.distance - first.distance;
    const rawWeight = Math.max(0, 1 - separation / REGION_BLEND_WIDTH);
    if (rawWeight > 0) totals.set(candidate.region.biome, (totals.get(candidate.region.biome) ?? 0) + rawWeight * rawWeight);
  }
  const sum = [...totals.values()].reduce((total, value) => total + value, 0);
  return Object.freeze([...totals.entries()]
    .map(([biome, weight]) => Object.freeze({ biome, weight: weight / sum }))
    .sort((a, b) => b.weight - a.weight || a.biome.localeCompare(b.biome)));
}
